import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { Spinner } from "@radix-ui/themes";
import formatPrice from "@/core/helpers/formatPrice";
import { imgSbankBgAtm } from "@/shared/assets/img";
import { useAtmQuery } from "@/domains/bank/hooks/useAtmQuery";
import { BankATMDepositScreenContainer } from "@/domains/bank/containers/atm/BankATMDepositScreenContainer";
import { BankATMTransferScreenContainer } from "@/domains/bank/containers/atm/BankATMTransferScreenContainer";
import { BankATMWithdrawScreenContainer } from "@/domains/bank/containers/atm/BankATMWithdrawScreenContainer";
import { BankATMBackButton } from "@/domains/bank/components/BankATM/components/BankATMBackButton";
import { BankATMFooter } from "@/domains/bank/components/BankATM/components/BankATMFooter";
import { BankATMHomeScreen } from "@/domains/bank/components/BankATM/components/BankATMHomeScreen";
import { BankATMScreen } from "@/domains/bank/components/BankATM/types";
import { BankBullet } from "@/domains/bank/components/BankBullet/BankBullet";
import { BankCard } from "@/domains/bank/components/BankCard/BankCard";
import { SBankLogo } from "@/domains/bank/components/BankCard/components/SbankLogo";
import { BankCharacter } from "@/domains/bank/types";

function UnderConstructionScreen({
  onCancel,
  atmId,
  maxCash,
  maxCard,
}: {
  onCancel: () => void;
  atmId: number;
  maxCash: number;
  maxCard: number;
}) {
  return (
    <div className="relative h-full flex flex-col">
      <BankATMBackButton onClick={onCancel} />

      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-dark-800 mb-2">
            Пополнение телефона временно недоступно
          </h2>
          <p className="text-sbank-muted">Пожалуйста, выберите другой раздел</p>
        </div>
      </div>
    </div>
  );
}

type BankATMProps = {
  bankCharacter: BankCharacter;
  hasAccount: boolean;
  isLoading: boolean;
  atmId: number;
};

export function BankATM({ bankCharacter, hasAccount, isLoading, atmId }: BankATMProps) {
  const [activeScreen, setActiveScreen] = useState<BankATMScreen>(BankATMScreen.Home);
  const atmQuery = useAtmQuery(atmId);

  if (!hasAccount) {
    return (
      <div
        className="relative overflow-hidden -mr-[3px] w-[900px] h-[715px] flex flex-col p-8 pb-7 m-auto rounded-[1rem] bg-white bg-cover font-tt-norms-pro bg-[top_center]"
        style={{
          backgroundImage: `url(${imgSbankBgAtm})`,
        }}
      >
        <header className="flex items-center justify-between pt-2 pb-8">
          <SBankLogo className="w-[140px]" />
        </header>

        <main className="h-full bg-gradient-to-b from-white to-[#e3f6e6] to-70% text-dark-800 rounded-[1rem] p-7 flex flex-col items-center justify-center">
          {isLoading ? (
            <Spinner className="w-8 h-8 animate-spin text-sbank-muted" />
          ) : (
            <>
              <div className="text-2xl font-bold text-dark-800 mb-2">У вас нет счета в банке</div>
              <p className="text-sbank-muted">Пожалуйста, откройте счет в ближайшем отделении.</p>
            </>
          )}
        </main>

        <BankATMFooter atmId={atmId} businessInfo={atmQuery.data?.businessInfo} />
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden -mr-[3px] w-[900px] h-[720px] flex flex-col p-8 pb-7 m-auto rounded-[1rem] bg-white bg-cover font-tt-norms-pro bg-[top_center]"
      style={{
        backgroundImage: `url(${imgSbankBgAtm})`,
      }}
    >
      <header className="flex items-center justify-between pt-2 pb-8">
        <SBankLogo className="w-[140px]" />
      </header>

      <main className="h-full bg-gradient-to-b from-white to-[#e3f6e6] to-70% text-dark-800 rounded-[1rem] p-7 flex flex-col">
        <div className="relative overflow-hidden mb-8 p-5 h-[76px] flex items-center text-white bg-gradient-to-r from-[#186D27] to-[#121212] rounded-[1rem]">
          <div className="flex gap-4">
            <BankBullet className="bg-white text-[#186d27]">₽</BankBullet>

            <div className="flex flex-col gap-1">
              <div className="text-18 font-bold leading-none text-white">
                {formatPrice(bankCharacter.balance)} ₽
              </div>
              <div className="text-11 text-white uppercase">Текущий баланс счета</div>
            </div>
          </div>

          <div className="absolute right-[20px] top-[20px] rounded-[0.875rem]">
            <BankCard.Template
              tier={bankCharacter.tariff.cardStyleAsset}
              name={bankCharacter.tariff.name}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeScreen}
            initial={{ opacity: 0, x: activeScreen !== BankATMScreen.Home ? 20 : -20 }}
            animate={{
              opacity: 1,
              x: 0,
              transition: {
                duration: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94],
              },
            }}
            exit={{
              opacity: 0,
              x: activeScreen !== BankATMScreen.Home ? 20 : -20,
              transition: {
                duration: 0.2,
                ease: [0.55, 0.06, 0.68, 0.19],
              },
            }}
            className="h-full"
          >
            {activeScreen === BankATMScreen.Home && (
              <BankATMHomeScreen
                onScreenChange={setActiveScreen}
                tariffName={bankCharacter.tariff.name}
                withdrawalFeePercent={atmQuery.data?.commission.commissions.withdraw.commission}
                transferFeePercent={atmQuery.data?.commission.commissions.transfer.commission}
              />
            )}

            {activeScreen === BankATMScreen.Transfer && (
              <BankATMTransferScreenContainer
                onCancel={() => setActiveScreen(BankATMScreen.Home)}
                atmId={atmId}
                maxCash={bankCharacter.cash}
                maxCard={bankCharacter.balance}
                feePercent={atmQuery.data?.commission.commissions.transfer.commission}
              />
            )}

            {activeScreen === BankATMScreen.Deposit && (
              <BankATMDepositScreenContainer
                onCancel={() => setActiveScreen(BankATMScreen.Home)}
                atmId={atmId}
                cashBalance={bankCharacter.cash}
              />
            )}

            {activeScreen === BankATMScreen.Withdraw && (
              <BankATMWithdrawScreenContainer
                onCancel={() => setActiveScreen(BankATMScreen.Home)}
                atmId={atmId}
                maxCard={bankCharacter.balance}
                cardBalance={bankCharacter.balance}
              />
            )}

            {activeScreen === BankATMScreen.PhoneTopUp && (
              <UnderConstructionScreen
                onCancel={() => setActiveScreen(BankATMScreen.Home)}
                atmId={atmId}
                maxCash={bankCharacter.cash}
                maxCard={bankCharacter.balance}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      <BankATMFooter atmId={atmId} businessInfo={atmQuery.data?.businessInfo} />
    </div>
  );
}
